import { Box, Typography, Container, Button } from '@mui/material';
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

const ContentPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    const pageContent = {
        'about-us': {
            title: 'About Us',
            content: (
                <>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        Varahi is a trusted service provider specializing in construction, renovation, demolition, earth excavation, rock cutting, and painting services. We are committed to delivering high-quality workmanship, safety-driven execution, and timely project completion.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        With a team of skilled professionals and modern equipment, Varahi handles projects of all sizes—from small residential works to large-scale commercial and industrial services. Our focus is on precision, transparency, and customer satisfaction at every stage of the project.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        We follow industry standards, safety protocols, and ethical practices to ensure reliable and cost-effective solutions. At Varahi, we don’t just build structures—we build trust.
                    </Typography>

                    <Typography variant="h5" fontWeight={600} color="#000" mt={3} mb={1}>
                        Our Core Services:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, color: 'text.secondary', lineHeight: 1.8 }}>
                        <li>Construction & Renovation</li>
                        <li>Demolition Services</li>
                        <li>Earth Excavation & Rock Cutting</li>
                        <li>Painting & Finishing Works</li>
                    </Box>

                    <Typography variant="h5" fontWeight={600} color="#000" mt={3} mb={1}>
                        Our Mission:
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        To deliver dependable, safe, and high-quality construction services that exceed customer expectations.
                    </Typography>

                    <Typography variant="h5" fontWeight={600} color="#000" mt={3} mb={1}>
                        Our Vision:
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        To become a leading infrastructure and construction service brand known for integrity, quality, and innovation.
                    </Typography>
                </>
            )
        },
        'privacy-policy': {
            title: 'Privacy Policy',
            content: (
                <>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        At Varahi, we respect your privacy and are committed to protecting your personal information.
                    </Typography>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        Information We Collect
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, color: 'text.secondary', lineHeight: 1.8 }}>
                        <li>Name, phone number, email address</li>
                        <li>Service request details</li>
                        <li>Location and site-related information</li>
                        <li>Payment-related details (processed securely via third-party gateways)</li>
                    </Box>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        How We Use Your Information
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, color: 'text.secondary', lineHeight: 1.8 }}>
                        <li>To respond to inquiries and service requests</li>
                        <li>To schedule inspections and services</li>
                        <li>To communicate updates and service information</li>
                        <li>To improve our website and services</li>
                    </Box>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        Data Protection
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        We implement appropriate security measures to safeguard your data. Personal information is never sold or shared with third parties except where required to deliver services or comply with legal obligations.
                    </Typography>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        Third-Party Services
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        Payment processing and analytics may be handled by secure third-party providers, each governed by their own privacy policies.
                    </Typography>

                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8} sx={{ fontStyle: 'italic', mt: 2 }}>
                        By using our website or services, you agree to this Privacy Policy.
                    </Typography>
                </>
            )
        },
        'terms-of-use': {
            title: 'Terms & Conditions',
            content: (
                <>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        By accessing or using Varahi’s website and services, you agree to the following terms and conditions:
                    </Typography>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        Service Scope
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, color: 'text.secondary', lineHeight: 1.8 }}>
                        <li>All services are provided based on site inspection and mutually agreed quotations.</li>
                        <li>Any additional work beyond the agreed scope may incur extra charges.</li>
                    </Box>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        Pricing & Payments
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, color: 'text.secondary', lineHeight: 1.8 }}>
                        <li>Prices are subject to change based on site conditions.</li>
                        <li>Advance payments may be required for certain services.</li>
                        <li>Final billing may be based on actual work completed (e.g., area, volume, or kilometers where applicable).</li>
                    </Box>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        Cancellations
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, color: 'text.secondary', lineHeight: 1.8 }}>
                        <li>Cancellations must be made before service commencement.</li>
                        <li>Cancellation charges may apply if work has already started or resources have been allocated.</li>
                    </Box>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        Liability
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, color: 'text.secondary', lineHeight: 1.8 }}>
                        <li>Varahi is not responsible for delays caused by unforeseen circumstances such as weather conditions, government restrictions, or force majeure events.</li>
                        <li>Clients are responsible for obtaining necessary approvals unless otherwise agreed.</li>
                    </Box>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        Intellectual Property
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        All content, logos, and materials on this website belong to Varahi and may not be used without permission.
                    </Typography>

                    <Typography variant="h6" fontWeight={600} color="#000" mt={3} mb={1}>
                        Changes to Terms
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph lineHeight={1.8}>
                        Varahi reserves the right to update these terms at any time without prior notice.
                    </Typography>
                </>
            )
        }
    };

    const currentContent = pageContent[slug] || {
        title: 'Page Not Found',
        content: <Typography>The page you requested could not be found.</Typography>
    };

    return (
        <Box>
            <Box sx={{ background: 'linear-gradient(90deg, #fff 60%, #FFF3E6 100%)' }}>
                <Container maxWidth="" sx={{}}>
                    <Navbar />
                </Container>
            </Box>
            <Container maxWidth="" sx={{ py: 8, minHeight: '60vh' }}>
                <Typography variant="h3" fontWeight={700} color="#ff6a00" mb={4}>
                    {currentContent.title}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {currentContent.content}
                </Box>

                <Button
                    variant="outlined"
                    onClick={() => navigate('/')}
                    sx={{ mt: 6, color: '#ff6a00', borderColor: '#ff6a00', '&:hover': { borderColor: '#e65c00', backgroundColor: '#fff5eb' } }}
                >
                    Back to Home
                </Button>
            </Container>
            <Footer />
        </Box>
    );
};

export default ContentPage;
